// councilAgents.js — Agent roster + color palette for The Council.
//
// Inspired by karpathy/llm-council: multiple LLMs deliberate, then a
// Chairman synthesizes a final answer. Each agent gets a distinct color
// (intentionally avoiding blue — that's the Apolo brand accent).

import { MODEL_REGISTRY } from './modelRegistry.js';

// DSP1-T02 — the council roster is DERIVED from MODEL_REGISTRY, never hardcoded
// bare IDs. The audit found the old roster used IDs like 'gpt-4o' /
// 'claude-opus-4-7' that are NOT in the provider-prefixed registry → findModel
// (exact match) failed → every agent 'unknown_model' → all_agents_failed_stage1.
// Each curated provider resolves to a REAL registry id (the preferred one, else
// that provider's first/flagship model); any entry that can't resolve is dropped.
const _registryById = new Map(MODEL_REGISTRY.map((m) => [m.id, m]));

function pickRegistryId(preferredId, provider) {
  if (_registryById.has(preferredId)) return preferredId;
  const flagship = MODEL_REGISTRY.find((m) => m.provider === provider);
  return flagship ? flagship.id : null;
}

// Curated display metadata + a preferred provider-prefixed id. `provider` is the
// registry's lowercase key (used to find a flagship fallback). Palette avoids
// blue (Apolo brand) and gold (Chairman).
const COUNCIL_CURATED = [
  { provider: 'anthropic', preferred: 'anthropic/claude-opus-5', name: 'Claude Opus 5', label: 'Anthropic', short: 'CL', color: '#ec4899', glow: 'rgba(236, 72, 153, 0.20)', emoji: '\u{1F338}' },
  { provider: 'openai', preferred: 'openai/gpt-5.6-sol', name: 'GPT-5.6 Sol', label: 'OpenAI', short: 'GP', color: '#10b981', glow: 'rgba(16, 185, 129, 0.20)', emoji: '\u{1F33F}' },
  { provider: 'deepseek', preferred: 'deepseek/deepseek-v4-pro', name: 'DeepSeek V4', label: 'DeepSeek', short: 'DS', color: '#06b6d4', glow: 'rgba(6, 182, 212, 0.20)', emoji: '\u{1F537}' },
  { provider: 'google', preferred: 'google/gemini-3.6-flash', name: 'Gemini 3.6 Flash', label: 'Google', short: 'GE', color: '#a855f7', glow: 'rgba(168, 85, 247, 0.20)', emoji: '\u{1F52E}' },
  { provider: 'xai', preferred: 'xai/grok-4.5', name: 'Grok 4.5', label: 'xAI', short: 'GR', color: '#f97316', glow: 'rgba(249, 115, 22, 0.20)', emoji: '\u{2604}\u{FE0F}' },
  { provider: 'mistral', preferred: 'mistral/mistral-medium-3-5', name: 'Mistral Medium 3.5', label: 'Mistral', short: 'MI', color: '#eab308', glow: 'rgba(234, 179, 8, 0.20)', emoji: '\u{1F32C}\u{FE0F}' },
];

export const COUNCIL_AGENTS = COUNCIL_CURATED
  .map((c) => {
    const id = pickRegistryId(c.preferred, c.provider);
    if (!id) return null;
    return { id, name: c.name, provider: c.label, short: c.short, color: c.color, glow: c.glow, emoji: c.emoji };
  })
  .filter(Boolean);

// Chairman: synthesizes the final answer. Gold / champagne — distinct from
// all council member colors so it reads as the "authority" tier.
export const CHAIRMAN_COLOR = '#fbbf24'; // amber-400
export const CHAIRMAN_GLOW = 'rgba(251, 191, 36, 0.25)';

// Sprint v11++ — palette pra agents adicionados dinamicamente via dropdown
// "Adicionar LLM". Cada modelo do modelRegistry que não esteja em
// COUNCIL_AGENTS pega uma cor desta paleta via hash do model id. As cores
// abaixo evitam blue (brand Apolo) e gold (chairman) — todas distintas das
// 12 cores hardcoded do roster acima.
const EXTRA_AGENT_PALETTE = [
  { color: '#f43f5e', glow: 'rgba(244, 63, 94, 0.20)', emoji: '🩷' },   // rose-500
  { color: '#84cc16', glow: 'rgba(132, 204, 22, 0.20)', emoji: '🌱' },  // lime-500
  { color: '#0ea5e9', glow: 'rgba(14, 165, 233, 0.20)', emoji: '💧' },  // sky-500
  { color: '#d946ef', glow: 'rgba(217, 70, 239, 0.20)', emoji: '✨' },  // fuchsia-500
  { color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.20)', emoji: '🔥' },  // amber-500
  { color: '#06b6d4', glow: 'rgba(6, 182, 212, 0.20)', emoji: '🧊' },   // cyan-500
  { color: '#a3e635', glow: 'rgba(163, 230, 53, 0.20)', emoji: '🍀' },  // lime-400
  { color: '#ec4899', glow: 'rgba(236, 72, 153, 0.20)', emoji: '💎' },  // pink-500
];

function hashStringToIndex(str, max) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % max;
}

function deriveShortFromId(id) {
  // Pega 2 letras significativas. Ex: 'mistral-large-3' → 'ML'.
  const parts = String(id).split(/[-/._]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  const slug = String(id).replace(/[^a-zA-Z]/g, '');
  return (slug[0] || '?').toUpperCase() + (slug[1] || '').toUpperCase();
}

/**
 * Build an agent record for a model id that isn't in COUNCIL_AGENTS.
 * Uses the model registry entry for name/provider when available, falls
 * back to defaults derived from the id.
 *
 * @param {string} modelId
 * @param {{id:string, provider?:string}|null} registryEntry
 * @returns {{id, name, provider, short, color, glow, emoji}}
 */
export function agentFromRegistryModel(modelId, registryEntry) {
  const idx = hashStringToIndex(modelId, EXTRA_AGENT_PALETTE.length);
  const palette = EXTRA_AGENT_PALETTE[idx];
  return {
    id: modelId,
    name: registryEntry?.id?.split('/').slice(-1)[0] || modelId,
    provider: registryEntry?.provider || 'custom',
    short: deriveShortFromId(modelId),
    color: palette.color,
    glow: palette.glow,
    emoji: palette.emoji,
  };
}

export function getAgent(id, extraRoster = []) {
  // 1) hardcoded roster first
  const fromHardcoded = COUNCIL_AGENTS.find((a) => a.id === id);
  if (fromHardcoded) return fromHardcoded;
  // 2) extra roster (dynamic additions)
  const fromExtra = (extraRoster || []).find((a) => a.id === id);
  if (fromExtra) return fromExtra;
  return null;
}
